// Docs' comments adapter. A new comment anchors to the selected text: the
// selection's start as `p:<hierarchical offset>` ("p:0;12;0" = section 1,
// block 13, character 0) and the text itself as the quote. Clicking a thread
// searches for the quote starting at that offset (then from the top, since
// edits move text around) and selects it.
//
// Showing a thread also marks its text with the editor's search highlight
// (Search.findAll → searchHighlighters): a paint-time overlay, like Find's —
// never character formatting, so the document isn't changed or marked
// unsaved. It's narrowed to the one occurrence the thread points at, and
// dropped when the caret leaves it, another thread is shown, or the Comments
// pane closes.

import type { DocumentEditorContainerComponent } from "@syncfusion/ej2-react-documenteditor";
import type { CommentAnchor, CommentsAdapter } from "@/features/drive-file/CommentsPanel";

type DocEditor = NonNullable<DocumentEditorContainerComponent["documentEditor"]>;

const MAX_QUOTE = 500;
const MAX_ANCHOR = 128;
const OFFSET_RE = /^\d+(?:;\d+)*$/;

export interface DocsCommentsAdapter extends CommentsAdapter {
    /** Call on every selectionChange: drops the mark once the caret leaves it. */
    selectionMoved: () => void;
    /** Another document was loaded: forget the mark without touching the editor. */
    reset: () => void;
}

/** Search can't cross paragraphs — a thread is found by its quote's first line. */
function firstLine(quote: string | null): string | undefined {
    return (quote ?? "").split("\n").map((l) => l.trim()).find((l) => l.length > 0)?.slice(0, 200);
}

function selectionRange(ed: DocEditor): { start: string; end: string } | null {
    try {
        return { start: ed.selection.startOffset, end: ed.selection.endOffset };
    } catch {
        return null;
    }
}

export function docsCommentsAdapter(getEditor: () => DocEditor | null | undefined): DocsCommentsAdapter {
    // The marked spot: where the selection was left, and whether a search
    // highlight was painted (only then is there anything to clear — so a
    // user's own Find results are never wiped by us).
    let mark: { start: string; end: string; painted: boolean } | null = null;
    let marking = false;
    let dropTimer = 0;
    const listeners = new Set<() => void>();

    const navigate = (anchor: string | null, quote: string | null): boolean => {
        const ed = getEditor();
        if (!ed) return false;
        const offset = anchor?.startsWith("p:") ? anchor.slice(2) : null;
        const placeAtAnchor = (): boolean => {
            if (!offset || !OFFSET_RE.test(offset)) return false;
            try {
                ed.selection.select(offset, offset);
                return true;
            } catch {
                return false; // the document changed shape since
            }
        };
        const needle = firstLine(quote);
        if (needle) {
            const starts: Array<() => boolean> = [
                placeAtAnchor,
                () => {
                    try {
                        ed.selection.moveToDocumentStart();
                        return true;
                    } catch {
                        return false;
                    }
                }
            ];
            for (const start of starts) {
                if (!start()) continue;
                try {
                    ed.search.find(needle, "None");
                    if ((ed.selection.text ?? "").trim().toLowerCase() === needle.toLowerCase()) {
                        ed.focusIn();
                        return true;
                    }
                } catch {
                    /* search unavailable — fall back to the offset */
                }
            }
        }
        if (placeAtAnchor()) {
            ed.focusIn();
            return true;
        }
        return false;
    };

    const dropMark = (notify: boolean) => {
        window.clearTimeout(dropTimer);
        const had = mark;
        mark = null;
        if (had?.painted) {
            try {
                getEditor()?.search.searchResults.clear();
            } catch {
                /* editor gone */
            }
        }
        if (had && notify) listeners.forEach((listener) => listener());
    };

    return {
        selectionHint: "Select text in the document to comment on it.",

        captureAnchor: (): CommentAnchor | null => {
            const ed = getEditor();
            if (!ed) return null;
            const sel = ed.selection;
            const text = (sel.text ?? "").replace(/\r\n?|\v/g, "\n").trim();
            if (!text) return null;
            const start = sel.isForward ? sel.startOffset : sel.endOffset;
            const anchor = `p:${start}`;
            return {
                anchor: OFFSET_RE.test(start) && anchor.length <= MAX_ANCHOR ? anchor : null,
                quote: text.slice(0, MAX_QUOTE)
            };
        },

        describeAnchor: (anchor) => {
            const m = /^p:(\d+);(\d+)/.exec(anchor);
            if (!m) return null;
            const section = Number(m[1]);
            const block = Number(m[2]);
            return section > 0 ? `Section ${section + 1}, paragraph ${block + 1}` : `Paragraph ${block + 1}`;
        },

        navigate,

        highlight: (anchor, quote) => {
            const ed = getEditor();
            if (!ed) return false;
            dropMark(false);
            // The selection moves several times below — none of that is "the
            // user moved away".
            marking = true;
            try {
                if (!navigate(anchor, quote)) return false;
                const range = selectionRange(ed);
                if (!range) return true;
                const needle = firstLine(quote);
                let painted = false;
                // Only when the quote itself was found — never mark whatever
                // now sits at a stale offset.
                if (needle && (ed.selection.text ?? "").trim().toLowerCase() === needle.toLowerCase()) {
                    try {
                        ed.search.findAll(needle, "None");
                        painted = true;
                        keepOnlyOccurrenceAt(ed, range.start);
                    } catch {
                        /* no highlight — the selection still shows the spot */
                    }
                    // findAll selects its first match; put the selection back.
                    try {
                        ed.selection.select(range.start, range.end);
                    } catch {
                        /* keep findAll's */
                    }
                }
                mark = { ...range, painted };
                return true;
            } finally {
                marking = false;
            }
        },

        clearHighlight: () => dropMark(false),

        subscribeHighlightCleared: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },

        selectionMoved: () => {
            if (!mark || marking) return;
            // Let the editor finish its selectionChange before repainting.
            window.clearTimeout(dropTimer);
            dropTimer = window.setTimeout(() => {
                const ed = getEditor();
                const range = ed ? selectionRange(ed) : null;
                if (mark && range && range.start === mark.start && range.end === mark.end) return;
                dropMark(true);
            }, 0);
        },

        reset: () => {
            window.clearTimeout(dropTimer);
            const had = mark;
            mark = null;
            if (had) listeners.forEach((listener) => listener());
        }
    };
}

/** Narrows findAll's highlight (every match) to the occurrence the thread points at. */
function keepOnlyOccurrenceAt(ed: DocEditor, start: string) {
    const results = ed.search.textSearchResults;
    const list = results?.innerList;
    if (!list || list.length < 2) return;
    const match = list.find((r) => r.startOffset === start);
    if (!match) return;
    list.splice(0, list.length, match);
    results.currentIndex = 0;
    ed.search.highlight(results);
}
